import { AnchorButton, Modal, Text } from '@payaca/ambrosia';
import UntitledIcon from '@payaca/untitled-icons';
import { FC, useEffect, useState } from 'react';

/**
 * Comfortably inside the two minutes the link itself lasts, so the modal is
 * gone well before the link it offers stops working.
 */
const AUTO_CLOSE_SECONDS = 60;

/**
 * Offers the preview link for the user to follow themselves, for the one case
 * the trigger cannot handle: the browser refused the tab it opened.
 *
 * A link the user clicks is not subject to the pop-up blocker, so this works
 * where `window.open` did not. It closes itself rather than waiting to be
 * dismissed, since a link left on screen outlives the link it points at.
 */
export const PopupBlockedModal: FC<{
  url: string | null;
  onClose: () => void;
}> = (props) => {
  const { url, onClose } = props;

  const [secondsLeft, setSecondsLeft] = useState(AUTO_CLOSE_SECONDS);

  useEffect(() => {
    if (!url) return undefined;

    setSecondsLeft(AUTO_CLOSE_SECONDS);
    const tick = setInterval(
      () => setSecondsLeft((remaining) => remaining - 1),
      1000
    );

    return () => clearInterval(tick);
  }, [url]);

  useEffect(() => {
    // `url` guards the loop: closing clears it, so this cannot fire again on a
    // re-render while the count sits at zero.
    if (url && secondsLeft <= 0) onClose();
  }, [url, secondsLeft, onClose]);

  return (
    <Modal isOpen={!!url} onClose={onClose} title="Open the preview">
      <Modal.Body>
        <Text>Your browser stopped us opening the preview in a new tab.</Text>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Actions>
          {/* Beside the action it applies to, so it reads as that link's
              lifetime rather than a note about the modal. */}
          <Text level="supporting">Expires in {Math.max(secondsLeft, 0)}s</Text>
          <AnchorButton
            href={url ?? ''}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onClose}
          >
            <UntitledIcon name="eye.3" className="size-4" />
            Open preview
          </AnchorButton>
        </Modal.Footer.Actions>
      </Modal.Footer>
    </Modal>
  );
};

export default PopupBlockedModal;
